import { useState } from "react";
import { PolicyLayout, useSettings, ConfigField } from "@/components/PolicyLayout";
import { api } from "@/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, MessageCircle } from "lucide-react";

export default function ContactUs() {
  const s = useSettings();
  const [f, setF] = useState({ name:"", email:"", phone:"", subject:"", message:"" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) { toast.error("Please fill name, email and message"); return; }
    setLoading(true);
    try {
      await api.post("/contact", f);
      setSent(true); setF({ name:"", email:"", phone:"", subject:"", message:"" });
      toast.success("Message sent! We'll respond within 24 hours.");
    } catch { toast.error("Could not send message. Please try again."); }
    finally { setLoading(false); }
  };

  const wa = (s.whatsapp || "").replace(/[^0-9]/g, "");
  return (
    <PolicyLayout title="Contact Us" kicker="We're here to help">
      <p>Have a question about a product, order, delivery, or a specific puja? Reach out to us through any of the channels below and our team will respond within one business day.</p>

      <div className="not-prose grid md:grid-cols-2 gap-6 mt-8">
        <div className="rounded-2xl border border-border bg-white p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-primary mt-0.5"/>
            <div>
              <div className="font-medium">Email</div>
              <div className="text-sm text-muted-foreground"><ConfigField value={s.email} label="business email"/></div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-primary mt-0.5"/>
            <div>
              <div className="font-medium">Phone / Customer Care</div>
              <div className="text-sm text-muted-foreground"><ConfigField value={s.phone} label="phone number"/></div>
            </div>
          </div>
          {wa && <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-primary mt-0.5"/>
            <div>
              <div className="font-medium">WhatsApp</div>
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="text-sm text-primary underline">Chat on WhatsApp</a>
            </div>
          </div>}
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary mt-0.5"/>
            <div>
              <div className="font-medium">Registered Address</div>
              <div className="text-sm text-muted-foreground"><ConfigField value={s.address} label="business address"/></div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-primary mt-0.5"/>
            <div>
              <div className="font-medium">Support Hours</div>
              <div className="text-sm text-muted-foreground">{s.support_hours}</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-border bg-white p-6 space-y-3" data-testid="contact-form">
          <h2 className="font-serif text-2xl m-0">Send us a message</h2>
          {sent && <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">Thank you! Your message has been received. We'll respond within one business day.</div>}
          <input data-testid="contact-name" required className="pk-input" placeholder="Your Name" value={f.name} onChange={e=>setF({...f, name:e.target.value})}/>
          <input data-testid="contact-email" required type="email" className="pk-input" placeholder="Email Address" value={f.email} onChange={e=>setF({...f, email:e.target.value})}/>
          <input data-testid="contact-phone" className="pk-input" placeholder="Phone (optional)" value={f.phone} onChange={e=>setF({...f, phone:e.target.value})}/>
          <input data-testid="contact-subject" className="pk-input" placeholder="Subject" value={f.subject} onChange={e=>setF({...f, subject:e.target.value})}/>
          <textarea data-testid="contact-message" required rows={5} className="pk-input" placeholder="How can we help?" value={f.message} onChange={e=>setF({...f, message:e.target.value})}/>
          <button data-testid="contact-submit-btn" disabled={loading} className="pk-btn-primary w-full">{loading?"Sending...":"Send Message"}</button>
          <div className="text-xs text-muted-foreground">By submitting, you agree to our <a href="/privacy" className="underline">Privacy Policy</a>.</div>
        </form>
      </div>
    </PolicyLayout>
  );
}
